/**
 * Édition d'une scorecard d'entretien structuré : validation et attribution
 * des IDs.
 *
 * Partagé entre Convex (qui fait foi à l'enregistrement) et l'éditeur (qui
 * affiche les mêmes erreurs avant l'envoi). Les types reprennent la forme des
 * validateurs `*ScorecardValidator` de `convex/schema.ts`, sans en dépendre :
 * ce paquet ne peut importer rien du monorepo (cf. `index.ts`).
 */

import { COMPETENCE_NEUTRE } from "./chartOption";

export type CritereScorecard = {
  criteriaId: string;
  description: string;
  points: number;
  followup: string;
};

export type QuestionScorecard = {
  questionId: string;
  type: string;
  texte: string;
  criteres: CritereScorecard[];
};

export type SousCompetenceScorecard = {
  nom: string;
  poids: number;
  questions: QuestionScorecard[];
};

export type CompetenceScorecard = {
  nom: string;
  poids: number;
  questions?: QuestionScorecard[];
  sousCompetences?: SousCompetenceScorecard[];
};

/** Ce que l'éditeur envoie : un élément ajouté n'a pas encore d'ID. */
export type CritereSaisi = Omit<CritereScorecard, "criteriaId"> & {
  criteriaId?: string;
};
export type QuestionSaisie = Omit<QuestionScorecard, "questionId" | "criteres"> & {
  questionId?: string;
  criteres: CritereSaisi[];
};
export type SousCompetenceSaisie = Omit<SousCompetenceScorecard, "questions"> & {
  questions: QuestionSaisie[];
};
export type CompetenceSaisie = Omit<
  CompetenceScorecard,
  "questions" | "sousCompetences"
> & {
  questions?: QuestionSaisie[];
  sousCompetences?: SousCompetenceSaisie[];
};

export type ContenuScorecardSaisi = {
  titre: string;
  description: string;
  competences: CompetenceSaisie[];
};

export type CompteursScorecard = {
  dernierIdQuestion: number;
  dernierIdCritere: number;
};

/** Écart relevé sur une scorecard ; `chemin` situe l'élément pour l'éditeur. */
export type ProblemeScorecard = { chemin: string; message: string };

export type RapportValidationScorecard = {
  /** Bloquent l'enregistrement. */
  erreurs: ProblemeScorecard[];
  /** Signalés, n'empêchent rien (poids historiques ≠ 1). */
  avertissements: ProblemeScorecard[];
};

/** Tolérance sur la somme des poids : 0,1 + 0,2 ≠ 0,3 en flottant. */
const TOLERANCE_POIDS = 1e-6;

type Groupe<Q> = { chemin: string; questions: Q[] };

/**
 * Groupes de questions d'une compétence : elle-même (forme plate) ou chacune
 * de ses sous-compétences (forme hiérarchique).
 */
function groupesDe<Q>(
  competence: { questions?: Q[]; sousCompetences?: { questions: Q[] }[] },
  chemin: string,
): Groupe<Q>[] {
  if (competence.sousCompetences?.length) {
    return competence.sousCompetences.map((sous, i) => ({
      chemin: `${chemin}.sousCompetences[${i}]`,
      questions: sous.questions,
    }));
  }
  return [{ chemin, questions: competence.questions ?? [] }];
}

function estVide(texte: string): boolean {
  return texte.trim() === "";
}

/**
 * Règles de contenu d'une scorecard. Ne regarde pas les IDs : leur contrôle
 * suppose l'historique, c'est `attribuerIdsScorecard`.
 */
export function validerScorecard(
  contenu: ContenuScorecardSaisi,
): RapportValidationScorecard {
  const erreurs: ProblemeScorecard[] = [];
  const avertissements: ProblemeScorecard[] = [];
  const erreur = (chemin: string, message: string) =>
    erreurs.push({ chemin, message });

  if (estVide(contenu.titre)) erreur("titre", "Le titre est obligatoire");
  if (contenu.competences.length === 0) {
    erreur("competences", "Au moins une compétence est requise");
  }

  contenu.competences.forEach((competence, ic) => {
    const cheminC = `competences[${ic}]`;
    if (estVide(competence.nom)) {
      erreur(cheminC, "Le nom de la compétence est obligatoire");
    }
    if (!Number.isFinite(competence.poids) || competence.poids < 0) {
      erreur(cheminC, "Le poids doit être un nombre positif ou nul");
    }
    // Le chatbot et la notation lisent l'une OU l'autre forme : une compétence
    // portant les deux verrait ses questions directes ignorées.
    if (competence.questions?.length && competence.sousCompetences?.length) {
      erreur(
        cheminC,
        "Une compétence porte soit des questions, soit des sous-compétences",
      );
    }
    competence.sousCompetences?.forEach((sous, is) => {
      const cheminS = `${cheminC}.sousCompetences[${is}]`;
      // Nom non exigé : la « Question neutre » de la v2 KFC a une
      // sous-compétence anonyme.
      if (!Number.isFinite(sous.poids) || sous.poids < 0) {
        erreur(cheminS, "Le poids doit être un nombre positif ou nul");
      }
    });

    for (const groupe of groupesDe(competence, cheminC)) {
      if (groupe.questions.length === 0) {
        erreur(groupe.chemin, "Au moins une question est requise");
      }
      groupe.questions.forEach((question, iq) => {
        const cheminQ = `${groupe.chemin}.questions[${iq}]`;
        if (estVide(question.texte)) {
          erreur(cheminQ, "Le texte de la question est obligatoire");
        }
        if (question.criteres.length === 0) {
          erreur(cheminQ, "Au moins un critère est requis");
        }
        question.criteres.forEach((critere, ik) => {
          const cheminK = `${cheminQ}.criteres[${ik}]`;
          if (estVide(critere.description)) {
            erreur(cheminK, "La description du critère est obligatoire");
          }
          // Négatif = pénalité, légitime. Zéro = critère sans effet.
          if (!Number.isFinite(critere.points) || critere.points === 0) {
            erreur(cheminK, "Les points doivent être non nuls");
          }
        });
      });
    }
  });

  // Somme hors « Question neutre » : elle ne compte pas dans le score. Des
  // scorecards en service sont à 0,75 et 0,90 — d'où un simple avertissement.
  const somme = contenu.competences
    .filter((c) => c.nom !== COMPETENCE_NEUTRE)
    .reduce((total, c) => total + c.poids, 0);
  if (Number.isFinite(somme) && Math.abs(somme - 1) > TOLERANCE_POIDS) {
    avertissements.push({
      chemin: "competences",
      message: `La somme des poids vaut ${Number(somme.toFixed(4))} au lieu de 1`,
    });
  }

  return { erreurs, avertissements };
}

/** Numéro d'une question `q<n>`, repris dans l'ID de ses critères. */
function prefixeCriteres(questionId: string): string {
  const numero = /^q(\d+)$/.exec(questionId)?.[1];
  return `c${numero ?? questionId}_`;
}

export type ResultatAttributionIds = {
  competences: CompetenceScorecard[];
  compteurs: CompteursScorecard;
  erreurs: ProblemeScorecard[];
};

/**
 * Donne un ID aux questions et critères ajoutés, et contrôle ceux fournis.
 *
 * `existants` : critères de chaque question de la version courante, par
 * `questionId`. Un ID fourni doit s'y trouver, une seule fois, et un critère
 * reste sous sa question (son ID en porte le numéro). Un ID absent de la
 * version courante est refusé même s'il a existé avant : c'est ce qui
 * empêche de le réattribuer à un autre contenu.
 *
 * Les nouveaux IDs sont tirés des compteurs monotones, jamais d'un « trou ».
 */
export function attribuerIdsScorecard(
  competences: CompetenceSaisie[],
  existants: ReadonlyMap<string, ReadonlySet<string>>,
  compteurs: CompteursScorecard,
): ResultatAttributionIds {
  const erreurs: ProblemeScorecard[] = [];
  let { dernierIdQuestion, dernierIdCritere } = compteurs;
  const questionsVues = new Set<string>();
  const criteresVus = new Set<string>();

  const questionAvecIds = (
    question: QuestionSaisie,
    chemin: string,
  ): QuestionScorecard => {
    let questionId = question.questionId;
    let criteresConnus: ReadonlySet<string> = new Set();
    // Question refusée : ses critères le sont avec elle, inutile de les lister.
    let questionInconnue = false;
    if (questionId === undefined) {
      questionId = `q${++dernierIdQuestion}`;
    } else if (!existants.has(questionId)) {
      erreurs.push({ chemin, message: `Question inconnue : ${questionId}` });
      questionInconnue = true;
    } else {
      if (questionsVues.has(questionId)) {
        erreurs.push({ chemin, message: `Question en double : ${questionId}` });
      }
      criteresConnus = existants.get(questionId) ?? new Set();
    }
    questionsVues.add(questionId);

    const prefixe = prefixeCriteres(questionId);
    const criteres = question.criteres.map((critere, ik) => {
      const cheminK = `${chemin}.criteres[${ik}]`;
      let criteriaId = critere.criteriaId;
      if (criteriaId === undefined) {
        criteriaId = `${prefixe}${++dernierIdCritere}`;
      } else if (!questionInconnue && !criteresConnus.has(criteriaId)) {
        erreurs.push({
          chemin: cheminK,
          message: `Critère inconnu pour la question ${questionId} : ${criteriaId}`,
        });
      } else if (criteresVus.has(criteriaId)) {
        erreurs.push({
          chemin: cheminK,
          message: `Critère en double : ${criteriaId}`,
        });
      }
      criteresVus.add(criteriaId);
      return { ...critere, criteriaId };
    });
    return { ...question, questionId, criteres };
  };

  const resultat = competences.map((competence, ic): CompetenceScorecard => {
    const cheminC = `competences[${ic}]`;
    const { questions, sousCompetences, ...reste } = competence;
    return {
      ...reste,
      ...(questions && {
        questions: questions.map((q, iq) =>
          questionAvecIds(q, `${cheminC}.questions[${iq}]`),
        ),
      }),
      ...(sousCompetences && {
        sousCompetences: sousCompetences.map((sous, is) => ({
          ...sous,
          questions: sous.questions.map((q, iq) =>
            questionAvecIds(
              q,
              `${cheminC}.sousCompetences[${is}].questions[${iq}]`,
            ),
          ),
        })),
      }),
    };
  });

  return {
    competences: resultat,
    compteurs: { dernierIdQuestion, dernierIdCritere },
    erreurs,
  };
}

/** Critères de chaque question, par `questionId` : entrée d'`attribuerIdsScorecard`. */
export function indexerIdsScorecard(
  competences: CompetenceScorecard[],
): Map<string, Set<string>> {
  const index = new Map<string, Set<string>>();
  for (const competence of competences) {
    for (const groupe of groupesDe(competence, "")) {
      for (const question of groupe.questions) {
        index.set(
          question.questionId,
          new Set(question.criteres.map((c) => c.criteriaId)),
        );
      }
    }
  }
  return index;
}

// ============================================
// CRÉATION ET PUBLICATION
// ============================================

/**
 * Format d'un slug : minuscules, chiffres, séparés par `-` ou `_` (les
 * modèles historiques mêlent les deux : `assistant-manager-d-unite-marchande_v4`).
 */
export const FORMAT_SLUG = /^[a-z0-9]+(?:[-_][a-z0-9]+)*$/;
export const LONGUEUR_MAX_SLUG = 80;

export function estSlugValide(slug: string): boolean {
  return slug.length <= LONGUEUR_MAX_SLUG && FORMAT_SLUG.test(slug);
}

/** « Commercial terrain (v2) » → `commercial-terrain-v2`. Jamais vide. */
export function slugDepuisTitre(titre: string): string {
  const slug = titre
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, LONGUEUR_MAX_SLUG)
    .replace(/-+$/, "");
  return slug || "scorecard";
}

export type StatutScorecard = "brouillon" | "publiee" | "modifiee";

/**
 * - `brouillon` : jamais publiée, inutilisable en campagne ;
 * - `publiee` : la version courante est celle en service ;
 * - `modifiee` : des versions de travail existent au-dessus de la publiée.
 */
export function statutScorecard(
  numeroCourant: number,
  numeroPublie: number | null,
): StatutScorecard {
  if (numeroPublie === null) return "brouillon";
  return numeroCourant === numeroPublie ? "publiee" : "modifiee";
}

/** Contenu d'une scorecard vierge : à compléter avant de pouvoir publier. */
export function squeletteScorecard(): CompetenceSaisie[] {
  return [
    {
      nom: "",
      poids: 1,
      questions: [
        {
          type: "",
          texte: "",
          criteres: [{ description: "", points: 1, followup: "" }],
        },
      ],
    },
  ];
}

/**
 * Contenu d'une scorecard créée par import (ou vierge) : garde les IDs
 * valides et uniques du fichier, attribue les autres.
 *
 * Valide = `q<n>` pour une question, `c<n>_<m>` pour un critère dont `<n>` est
 * le numéro de SA question. Une question dont l'ID est refusé perd aussi ceux
 * de ses critères : leur préfixe ne correspondrait plus.
 *
 * Pas d'historique à protéger ici (la scorecard n'existe pas encore) : rien
 * n'est refusé, les IDs douteux sont simplement réattribués.
 */
export function preparerImportScorecard(
  competences: CompetenceSaisie[],
): { competences: CompetenceScorecard[]; compteurs: CompteursScorecard } {
  const questionsVues = new Set<string>();
  const criteresVus = new Set<string>();
  let dernierIdQuestion = 0;
  let dernierIdCritere = 0;

  const nettoyerQuestion = (question: QuestionSaisie): QuestionSaisie => {
    const numero = question.questionId
      ? /^q(\d+)$/.exec(question.questionId)?.[1]
      : undefined;
    const garderQuestion =
      numero !== undefined && !questionsVues.has(question.questionId ?? "");
    if (garderQuestion) {
      questionsVues.add(question.questionId as string);
      dernierIdQuestion = Math.max(dernierIdQuestion, Number(numero));
    }
    const criteres = question.criteres.map((critere): CritereSaisi => {
      const m = critere.criteriaId
        ? /^c(\d+)_(\d+)$/.exec(critere.criteriaId)
        : null;
      const garder =
        garderQuestion &&
        m !== null &&
        m[1] === numero &&
        !criteresVus.has(critere.criteriaId ?? "");
      if (!garder) {
        const { criteriaId: _ignore, ...reste } = critere;
        return reste;
      }
      criteresVus.add(critere.criteriaId as string);
      dernierIdCritere = Math.max(dernierIdCritere, Number(m[2]));
      return critere;
    });
    if (garderQuestion) return { ...question, criteres };
    const { questionId: _ignore, ...reste } = question;
    return { ...reste, criteres };
  };

  const nettoyees = competences.map((competence): CompetenceSaisie => {
    const { questions, sousCompetences, ...reste } = competence;
    return {
      ...reste,
      ...(questions && { questions: questions.map(nettoyerQuestion) }),
      ...(sousCompetences && {
        sousCompetences: sousCompetences.map((sous) => ({
          ...sous,
          questions: sous.questions.map(nettoyerQuestion),
        })),
      }),
    };
  });

  // Les IDs gardés sont « connus », sous leur question : `attribuerIdsScorecard`
  // les accepte et numérote le reste après les compteurs.
  const existants = new Map<string, Set<string>>();
  for (const competence of nettoyees) {
    for (const groupe of groupesDe(competence, "")) {
      for (const question of groupe.questions) {
        if (!question.questionId) continue;
        existants.set(
          question.questionId,
          new Set(
            question.criteres
              .map((c) => c.criteriaId)
              .filter((id): id is string => id !== undefined),
          ),
        );
      }
    }
  }
  const { competences: avecIds, compteurs } = attribuerIdsScorecard(
    nettoyees,
    existants,
    { dernierIdQuestion, dernierIdCritere },
  );
  return { competences: avecIds, compteurs };
}
